import { TacticIconsDictionary } from '@gettactic/components/src/icons/tactic-icons/dictionary';
import { Properties } from 'csstype';
import Konva from 'konva';
import React from 'react';
import { SpotShape, TextShape } from '../../../types';
import {
  createFurnitureElement,
  createIconElement,
  createNodeShape,
  createText
} from '../../../utils/shapeFactory';
import { useMapCanvas } from '../contexts/MapCanvasContext';
import { useMapEditor } from '../contexts/MapEditorContext';
import { useSpotDrawing } from '../editor/spot-numbering/useSpotDrawing';
import { generateNextSpotLabels } from '../helpers/generateNextSpotLabels';
import { getShapeRootTarget } from '../helpers/getRootTarget';
import { useAltToolsListener } from './useAltToolsListener';
import { useSpotLabelIterator } from './useSpotLabelIterator';

export interface UseMapCanvasHandlersOptions {
  iconToolGhostRef: React.MutableRefObject<Konva.Group | null>;
  stageRef: React.MutableRefObject<Konva.Stage | null>;
  transformerRef: React.MutableRefObject<Konva.Transformer | null>;
}

export interface UseMapCanvasHandlersReturn {
  onWheel: (event: Konva.KonvaEventObject<WheelEvent>) => void;
  onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onMouseDown: (event: Konva.KonvaPointerEvent) => void;
  onMouseMove: (event: Konva.KonvaPointerEvent) => void;
  onMouseEnter: () => void;
  onMouseUp: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onMouseLeave: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onClick: (event: Konva.KonvaPointerEvent) => void;
  onTap: (event: Konva.KonvaPointerEvent) => void;
  handleMultiDragStart: () => void;
  handleMultiDragMove: (event: Konva.KonvaEventObject<DragEvent>) => void;
  handleMultiDragEnd: () => void;
  isPanToolActive: boolean;
  isMouseIn: boolean;
  isDrawing: boolean;
  drawingPoints: number[];
  cursor: Properties['cursor'];
}

export const useMapEditorHandlers = (
  options: UseMapCanvasHandlersOptions
): UseMapCanvasHandlersReturn => {
  const { iconToolGhostRef, stageRef } = options;

  const {
    shapes,
    selectedIds,
    handleDrawStart: onDrawStart,
    handleDrawEnd: onDrawEnd,
    drawTool,
    activeTool: tool,
    furnitureToolSelectedFurniture,
    updateShape: onShapeUpdate,
    setSelectedIds: onSelectionChange,
    iconToolSelectedIcon,
    shouldPreventDeselect,
    setPreviewShape,
    highlightedShape,
    setHighlightedShape
  } = useMapEditor();

  const {
    editingZoneWidth,
    editingZoneHeight,
    minSelectionDistance,
    selectionBox,
    selectionStart,
    isDragging,
    shapesInSelection,
    dragStartPositions,
    setSelectionBox,
    setSelectionStart,
    setIsDragging,
    setShapesInSelection,
    setDragStartPositions,
    setShapes,
    stagePosition,
    lastSpotLabel,
    setLastSpotLabel,
    spotLabelPattern,
    updateStagePosition: onStageChange
  } = useMapCanvas();

  const wheelTimeoutRef = React.useRef<number | null>(null);

  const spotLabel = useSpotLabelIterator();

  const { isAltPaneActive } = useAltToolsListener();

  const isSelecting = React.useRef(false);

  const [isMouseIn, setIsMouseIn] = React.useState(false);

  const isPanToolActive = tool === 'pan' || isAltPaneActive;

  const isGhostItemEnabled =
    (tool === 'icon' && iconToolSelectedIcon) ||
    (tool === 'furniture' && furnitureToolSelectedFurniture) ||
    tool === 'spot';

  const cursor = isPanToolActive
    ? 'grab'
    : tool === 'draw' || tool === 'numbering' || tool === 'draw-select'
      ? 'crosshair'
      : tool === 'text'
        ? 'text'
        : tool === 'node-style'
          ? 'cell'
          : 'default';
  const {
    isDrawing,
    points: drawingPoints,
    handlers: drawSelectHandlers
  } = useSpotDrawing({
    stagePosition,
    onDrawEnd: (hitSpots, activeTool) => {
      const editedShapes: Record<string, SpotShape> = {};
      const selectedIds: string[] = [];

      let lastSpotNumber: string[] | null = lastSpotLabel || [];

      hitSpots.forEach(({ id }) => {
        if (activeTool === 'draw-select') {
          selectedIds.push(id);
          return;
        }
        const shape = shapes.find((s) => {
          return s.id === id;
        });

        if (shape?.type === 'spot') {
          if (!editedShapes[shape.id]) editedShapes[shape.id] = shape;

          lastSpotNumber = generateNextSpotLabels(
            spotLabelPattern,
            lastSpotNumber || []
          );

          if (lastSpotNumber)
            editedShapes[shape.id].label = lastSpotNumber.join('');
        }
      });

      setLastSpotLabel(lastSpotNumber);

      if (activeTool === 'draw-select') {
        onSelectionChange(selectedIds);
        return;
      }

      Object.entries(editedShapes).forEach(([id, shape]) => {
        onShapeUpdate(id, shape);
      });
    }
  });

  // Update transformer nodes when selection changes
  React.useEffect(() => {
    if (options.transformerRef.current && selectedIds.length > 1) {
      const stage = stageRef.current;
      if (!stage) return;

      const nodes = selectedIds
        .map((id) => stage.findOne(`#shape-${id}`))
        .filter(Boolean);

      options.transformerRef.current.nodes(nodes as never);
      options.transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedIds]);

  const updateShapesInSelection = React.useCallback(
    (selectionBox: { x: number; y: number; width: number; height: number }) => {
      const selectedShapes = shapes.filter((shape) => {
        if (shape.locked || shape.hidden) return false;

        const shapeRight = shape.x + shape.width;
        const shapeBottom = shape.y + shape.height;
        const boxRight = selectionBox.x + selectionBox.width;
        const boxBottom = selectionBox.y + selectionBox.height;

        return (
          shape.x < boxRight &&
          shapeRight > selectionBox.x &&
          shape.y < boxBottom &&
          shapeBottom > selectionBox.y
        );
      });

      setShapesInSelection(selectedShapes.map((shape) => shape.id));
    },
    [shapes, setShapesInSelection]
  );

  const updateIconToolGhostPosition = (position?: {
    x: number;
    y: number;
    scale: number;
  }) => {
    if (isGhostItemEnabled && iconToolGhostRef.current) {
      const stage = stageRef.current;
      const pos = stage?.getPointerPosition();
      if (!pos || !stage) return;

      // Adjust position based on scale and stage offset
      const scale = position ? position.scale : stage.scaleX(); // Assuming scaleX and scaleY are the same
      const newPos = {
        x: (pos.x - (position ? position.x : stage.x())) / scale,
        y: (pos.y - (position ? position.y : stage.y())) / scale
      };

      // Set the position of the ghost element
      iconToolGhostRef.current.position(newPos);

      // Keep the ghost size constant regardless of stage zoom
      iconToolGhostRef.current.scale({
        x: 1 / scale,
        y: 1 / scale
      });

      iconToolGhostRef.current.getLayer()?.batchDraw();
    }
  };

  const handleWheel = React.useCallback(
    (event: Konva.KonvaEventObject<WheelEvent>) => {
      event.evt.preventDefault();

      const stagePosition = {
        x: stageRef.current?.x() || 0,
        y: stageRef.current?.y() || 0,
        scale: stageRef.current?.scaleX() || 1
      };

      const stage = stageRef.current;
      if (!stage) return;

      if (event.evt.altKey || isPanToolActive) {
        const oldScale = stagePosition.scale;
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        const mousePointTo = {
          x: (pointer.x - stagePosition.x) / oldScale,
          y: (pointer.y - stagePosition.y) / oldScale
        };

        const newScale = event.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
        const boundedScale = Math.min(Math.max(newScale, 0.1), 5);

        if (boundedScale !== stagePosition.scale) {
          const newPos = {
            x: pointer.x - mousePointTo.x * boundedScale,
            y: pointer.y - mousePointTo.y * boundedScale,
            scale: boundedScale
          };
          stage.setPosition(newPos);
          stage.scale({
            x: boundedScale,
            y: boundedScale
          });

          if (wheelTimeoutRef.current) {
            clearTimeout(wheelTimeoutRef.current);
          }

          wheelTimeoutRef.current = window.setTimeout(() => {
            onStageChange(newPos);
          }, 100);

          if (isGhostItemEnabled) {
            updateIconToolGhostPosition(newPos);
          }
        }
      } else {
        const panIntensity = 1 / Math.max(stagePosition.scale, 0.5);
        const newPos = {
          x: stagePosition.x - event.evt.deltaX * panIntensity,
          y: stagePosition.y - event.evt.deltaY * panIntensity,
          scale: stagePosition.scale
        };

        stage.setPosition(newPos);

        if (wheelTimeoutRef.current) {
          clearTimeout(wheelTimeoutRef.current);
        }

        wheelTimeoutRef.current = window.setTimeout(() => {
          onStageChange(newPos);
        }, 100);

        if (isGhostItemEnabled) {
          updateIconToolGhostPosition(newPos);
        }
      }
    },
    [
      stagePosition,
      onStageChange,
      tool,
      editingZoneWidth,
      editingZoneHeight,
      isGhostItemEnabled
    ]
  );

  const handleDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (isPanToolActive) {
      const newPos = event.target.position();
      onStageChange({
        x: newPos.x,
        y: newPos.y,
        scale: stagePosition.scale
      });
    }
  };

  const handleMouseDown = (event: Konva.KonvaPointerEvent) => {
    if (isPanToolActive) {
      return;
    }

    if (tool === 'numbering' || tool === 'draw-select') {
      drawSelectHandlers.onMouseDown(event);
      return;
    }

    if (tool === 'draw') {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = {
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      };
      onDrawStart(transformedPoint);
      return;
    }

    if (tool === 'spot') {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };
      const newShape = createNodeShape({
        ...transformed,
        text: spotLabel.next(true).join('')
      }) as SpotShape;
      newShape.layerType = 'spots';
      setShapes((prev) => [...prev, newShape]);
    }

    if (tool === 'text') {
      const stage = stageRef.current;
      if (!stage) return;

      const clickOnText = event.target.attrs.id.startsWith('shape-text');
      if (clickOnText || highlightedShape?.shape.type === 'text') {
        if (clickOnText) {
          const textShapeId = event.target.attrs.id.replace(/^shape-/, '');
          const shape = shapes.find((s) => s.id === textShapeId);
          if (shape) {
            setHighlightedShape({
              shape,
              targetId: shape.id
            });
          }
        } else {
          setHighlightedShape(null);
        }
        return;
      }

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };
      const newShape = createText(transformed) as TextShape;
      newShape.text = 'Text';

      newShape.layerType = 'spots';
      setShapes((prev) => [...prev, newShape]);
    }

    if (tool === 'icon' && iconToolSelectedIcon) {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };
      const iconText =
        iconToolSelectedIcon.set === 'tactic' &&
        iconToolSelectedIcon.icon in TacticIconsDictionary.tactic.icons
          ? TacticIconsDictionary.tactic.icons?.[iconToolSelectedIcon.icon] ||
            ''
          : '';
      const newShape = createIconElement(
        transformed,
        iconToolSelectedIcon,
        String(iconText.toUpperCase())
      );

      newShape.layerType = 'shapes';
      setShapes((prev) => [...prev, newShape]);
    }

    if (tool === 'furniture' && furnitureToolSelectedFurniture) {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };
      const newShape = createFurnitureElement(
        transformed,
        furnitureToolSelectedFurniture
      );

      newShape.layerType = 'shapes';
      newShape.width = furnitureToolSelectedFurniture.width || 100;
      newShape.height = furnitureToolSelectedFurniture.height || 100;
      setShapes((prev) => [...prev, newShape]);
    }

    if (tool !== 'select' || event.evt.altKey) return;

    const clickedTarget = getShapeRootTarget(
      shapes,
      event.target as Konva.Shape
    );

    const clickedOnShape =
      clickedTarget !== null && clickedTarget.locked !== true;

    if (clickedOnShape) {
      isSelecting.current = false;
      return;
    }

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const transformed = {
      x: (pos.x - stagePosition.x) / stagePosition.scale,
      y: (pos.y - stagePosition.y) / stagePosition.scale
    };

    setSelectionStart(transformed);
    setIsDragging(false);
    setSelectionBox(null);
    setShapesInSelection([]);
    //isSelecting.current = true;
  };

  // Add state to track if shift key is pressed
  const [isShiftPressed, setIsShiftPressed] = React.useState(false);

  // Add key event listeners for shift key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
        // Update preview shape if drawing is in progress
        if (tool === 'draw' && drawTool.startPoint) {
          updatePreviewShapeWithShift(true);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
        // Update preview shape if drawing is in progress
        if (tool === 'draw' && drawTool.startPoint) {
          updatePreviewShapeWithShift(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [tool, drawTool.startPoint]);

  // Helper function to update preview shape with shift state
  const updatePreviewShapeWithShift = (shiftPressed: boolean) => {
    if (!drawTool.startPoint) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const transformedPoint = {
      x: (pos.x - stagePosition.x) / stagePosition.scale,
      y: (pos.y - stagePosition.y) / stagePosition.scale
    };

    const width = transformedPoint.x - drawTool.startPoint.x;
    const height = transformedPoint.y - drawTool.startPoint.y;

    // Calculate preview position based on drag direction
    const previewX = width < 0 ? transformedPoint.x : drawTool.startPoint.x;
    const previewY = height < 0 ? transformedPoint.y : drawTool.startPoint.y;

    // If shift key is pressed, make width and height equal
    let finalWidth = Math.abs(width);
    let finalHeight = Math.abs(height);

    if (shiftPressed) {
      // Use the larger dimension to ensure the shape is at least as large as the user's drag
      const maxDimension = Math.max(finalWidth, finalHeight);
      finalWidth = maxDimension;
      finalHeight = maxDimension;
    }

    setPreviewShape({
      x: previewX,
      y: previewY,
      width: finalWidth,
      height: finalHeight,
      type: drawTool.selectedShape
    });
  };

  const handleMouseMove = (event: Konva.KonvaPointerEvent) => {
    if (isPanToolActive) {
      return;
    }

    if (tool === 'numbering' || tool === 'draw-select') {
      drawSelectHandlers.onMouseMove(event);
      return;
    }

    if (tool === 'draw' && drawTool.startPoint) {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = {
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      };

      const width = transformedPoint.x - drawTool.startPoint.x;
      const height = transformedPoint.y - drawTool.startPoint.y;

      // Calculate preview position based on drag direction
      const previewX = width < 0 ? transformedPoint.x : drawTool.startPoint.x;
      const previewY = height < 0 ? transformedPoint.y : drawTool.startPoint.y;

      // If shift key is pressed, make width and height equal
      let finalWidth = Math.abs(width);
      let finalHeight = Math.abs(height);

      if (event.evt.shiftKey) {
        // Use the larger dimension to ensure the shape is at least as large as the user's drag
        const maxDimension = Math.max(finalWidth, finalHeight);
        finalWidth = maxDimension;
        finalHeight = maxDimension;
      }

      setPreviewShape({
        x: previewX,
        y: previewY,
        width: finalWidth,
        height: finalHeight,
        type: drawTool.selectedShape
      });

      return;
    }

    if (isGhostItemEnabled) {
      updateIconToolGhostPosition();
      return;
    }

    if (!selectionStart || tool !== 'select' || event.evt.altKey) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const transformed = {
      x: (pos.x - stagePosition.x) / stagePosition.scale,
      y: (pos.y - stagePosition.y) / stagePosition.scale
    };

    const dx = transformed.x - selectionStart.x;
    const dy = transformed.y - selectionStart.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > minSelectionDistance) {
      const newSelectionBox = {
        x: Math.min(selectionStart.x, transformed.x),
        y: Math.min(selectionStart.y, transformed.y),
        width: Math.abs(transformed.x - selectionStart.x),
        height: Math.abs(transformed.y - selectionStart.y)
      };

      setIsDragging(true);
      setSelectionBox(newSelectionBox);
      updateShapesInSelection(newSelectionBox);
    }
  };

  const handleMouseEnter = () => {
    setIsMouseIn(true);
  };

  const clearSelection = () => {
    setSelectionStart(null);
    setSelectionBox(null);
    setIsDragging(false);
    setShapesInSelection([]);
    isSelecting.current = false;
  };

  const handleMouseUp = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (isPanToolActive) {
      return;
    }

    if (e.type === 'mouseleave') {
      setIsMouseIn(false);
    }

    if (tool === 'draw' && drawTool.startPoint) {
      const stage = e.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = {
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      };

      // If shift key is pressed, ensure equal width and height
      if (e.evt.shiftKey) {
        const width = transformedPoint.x - drawTool.startPoint.x;
        const height = transformedPoint.y - drawTool.startPoint.y;

        // Calculate the larger dimension
        const maxDimension = Math.max(Math.abs(width), Math.abs(height));

        // Adjust the end point to maintain equal dimensions
        if (Math.abs(width) > Math.abs(height)) {
          // Width is larger, adjust height
          transformedPoint.y =
            drawTool.startPoint.y + (height < 0 ? -maxDimension : maxDimension);
        } else {
          // Height is larger, adjust width
          transformedPoint.x =
            drawTool.startPoint.x + (width < 0 ? -maxDimension : maxDimension);
        }
      }

      onDrawEnd(drawTool.startPoint, transformedPoint, drawTool.selectedShape);
      setPreviewShape(null);
      return;
    }

    if (tool === 'numbering' || tool === 'draw-select') {
      drawSelectHandlers.onMouseUp(e);
      return;
    }

    if (!selectionStart || tool !== 'select') return;

    if (isDragging && selectionBox) {
      onSelectionChange(shapesInSelection);
    }

    clearSelection();
  };

  const handleClick = React.useCallback(
    (event: Konva.KonvaPointerEvent) => {
      if (tool !== 'select' || event.evt.altKey) return;

      if (isDragging && selectionBox) {
        clearSelection();
        return;
      }

      const clickedTarget = getShapeRootTarget(
        shapes,
        event.target as Konva.Shape
      );

      const clickedOnShape =
        clickedTarget !== null && clickedTarget.locked !== true;

      if (!clickedOnShape && !isSelecting.current) {
        onSelectionChange([]);
      }

      shouldPreventDeselect.current = false;
    },
    [onSelectionChange, tool]
  );

  const handleMultiDragStart = () => {
    const positions: { [key: string]: { x: number; y: number } } = {};
    selectedIds.forEach((id) => {
      const shape = shapes.find((s) => s.id === id);
      if (shape) {
        positions[id] = { x: shape.x, y: shape.y };
      }
    });
    setDragStartPositions(positions);
  };

  const handleMultiDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {};

  const handleDragEnd = (event: Konva.KonvaEventObject<DragEvent>) => {
    const transformer = event.target;
    const dx = transformer.x();
    const dy = transformer.y();

    selectedIds.forEach((id) => {
      const startPos = dragStartPositions[id];
      if (startPos) {
        onShapeUpdate(id, {
          x: startPos.x + dx,
          y: startPos.y + dy
        });
      }
    });
  };

  const handleMultiDragEnd = () => {
    setDragStartPositions({});
  };

  React.useEffect(() => {
    const message = 'You have unsaved changes. Are you sure you want to leave?';
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message; // Some browsers use this message
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return {
    onWheel: handleWheel,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseEnter: handleMouseEnter,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
    onClick: handleClick,
    onTap: handleClick,
    handleMultiDragStart,
    handleMultiDragMove,
    handleMultiDragEnd,
    isPanToolActive,
    isMouseIn,
    isDrawing,
    drawingPoints,
    cursor
  };
};
